import * as DialogPrimitive from "@radix-ui/react-dialog";

import * as React from "react";

import { cn } from "@/lib/utils";

import { DialogOverlay } from "./Dialog";

const Dialog = DialogPrimitive.Root;

const DialogPortal = DialogPrimitive.Portal;

const MinimalDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        [
          "fixed",
          "left-[50%]",
          "top-[50%]",
          "z-50",
          "grid",
          "translate-x-[-50%]",
          "translate-y-[-50%]",
          "gap-4",
          "border",
          "border-primary",
          "bg-white",
          "shadow-lg",
          "duration-200",
          "data-[state=open]:animate-in",
          "data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0",
          "data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95",
          "data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-left-1/2",
          "data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=open]:slide-in-from-left-1/2",
          "data-[state=open]:slide-in-from-top-[48%]",
          "sm:rounded-lg",
        ],
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
MinimalDialogContent.displayName = DialogPrimitive.Content.displayName;

interface MinimalDialogProps {
  children: React.ReactNode;
  isOpen: boolean;
  onOverlayClick: () => void;
  onEscapeKeyDown: () => void;
}
/**
 * Minimal dialog component
 * @param children The content of the dialog
 * @param isOpen The state of the dialog
 * @param onOverlayClick The function to be called when the overlay is clicked
 * @param onEscapeKeyDown The function to be called when the escape key is pressed
 * @returns
 */
const MinimalDialog = ({
  children,
  isOpen,
  onOverlayClick,
  onEscapeKeyDown,
}: MinimalDialogProps) => (
  <Dialog open={isOpen}>
    <DialogPortal>
      <DialogOverlay />
      <MinimalDialogContent
        onPointerDownOutside={onOverlayClick}
        onEscapeKeyDown={onEscapeKeyDown}
      >
        {children}
      </MinimalDialogContent>
    </DialogPortal>
  </Dialog>
);

export { MinimalDialog };
